import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { withTheme } from 'styled-components'
import { Column } from '@sainsburys-tech/bolt'
import { Container, FlexColumn, SliderWrapper } from './UGLayout.styles'
import { useWindowDimensions } from './hooks'
import { GetComponent } from '../components/CMSComponents'
import UGSliderLayout from './UGSliderLayout'

const getColumnProps = (gridType, layout, flipOrientation, numberOfComponents, index, isVertical) => {
  const xsWidthSlider = numberOfComponents === 3 ? 4 : 6
  const xsWidth = layout === 'stack' ? 12 : xsWidthSlider
  const largerScreen = layout === 'slider' ? 'md' : 'sm'
  const largeColWidth = numberOfComponents === 2 ? 8 : 6
  const smallColWidth = numberOfComponents === 2 ? 4 : 3
  const largeCol = { [largerScreen]: largeColWidth, xs: xsWidth }
  const smallCol = { [largerScreen]: smallColWidth, xs: xsWidth }

  if (numberOfComponents === 1) {
    return { sm: 12, xs: 12 }
  }
  if (gridType === 'ug002') {
    const smWidth = numberOfComponents === 4 ? 6 : 12 / numberOfComponents
    const lgWidth = numberOfComponents > 4 ? 3 : 12 / numberOfComponents
    return { xs: xsWidth, sm: isVertical ? 12 : smWidth, lg: isVertical ? 12 : lgWidth }
  }
  if (gridType === 'ug004') {
    return { xs: 6, sm: 4, lg: 3 }
  }
  if (flipOrientation) {
    if (index === numberOfComponents - 1) return largeCol

    return smallCol
  }

  if (index === 0) return largeCol

  return smallCol
}

const UGLayout = ({
  consumingGridType,
  categoryId,
  included,
  mobileLayout,
  onLinkClick,
  flipOrientation,
  imageStyle,
  theme,
  layout,
  isVertical,
  childrenArray,
  meganav
}) => {
  const gridType = consumingGridType.toLowerCase()
  const numberOfComponents = included?.length || childrenArray?.length
  const tabletImagePositionTop = numberOfComponents > 1
  const layoutProp = numberOfComponents === 1 ? 'stack' : mobileLayout || layout
  const [hasScroll, setScroll] = useState(layoutProp === 'slider' || numberOfComponents > 4)

  const mobileLayoutBreakpoint = '575.98'
  const { screenWidth } = useWindowDimensions()
  useEffect(() => {
    const shouldHaveScroll = screenWidth <= mobileLayoutBreakpoint ? layoutProp === 'slider' : numberOfComponents > 4
    setScroll(shouldHaveScroll)
  }, [screenWidth, mobileLayout, layout])

  const ComponentWrapper = consumingGridType === 'ug004' ? Column : FlexColumn
  const includedComponents = included || childrenArray

  const content = includedComponents.map((child, i) => {
    const columnProps = getColumnProps(
      consumingGridType,
      mobileLayout,
      flipOrientation,
      numberOfComponents,
      i,
      isVertical
    )
    const gutterValue = i === 0 || isVertical ? 0 : 1

    const componentProps = {
      categoryId,
      onLinkClick: child.onLinkClick || onLinkClick,
      consumingGridType,
      imageStyle,
      tabletImagePositionTop,
      dropShadow: consumingGridType === 'ug002' && hasScroll,
      isOnlyComponentInGrid: numberOfComponents === 1 && !meganav
    }
    return (
      <ComponentWrapper
        key={`${gridType}-${i}`}
        data-test={`${gridType}-component-container-${i}`}
        numberOfComponents={numberOfComponents}
        consumingGridType={consumingGridType}
        xsGutterValue={layoutProp === 'stack' ? 0 : gutterValue}
        smGutterValue={gutterValue}
        gutterValue={consumingGridType === 'ug004' ? 1 : 0}
        mobileLayout={layoutProp}
        isSainsburys={theme.channel === 'sainsburys'}
        isVertical={isVertical}
        {...columnProps}
      >
        {included ? (
          <GetComponent {...{ ...child, ...componentProps }} positionInGrid={i} />
        ) : (
          React.cloneElement(child, { positionInGrid: i, ...componentProps })
        )}
      </ComponentWrapper>
    )
  })

  if (consumingGridType === 'ug004' && layout === 'slider') {
    return <UGSliderLayout included={included} consumingGridType={consumingGridType} />
  }

  const gridContainer = (
    <Container
      data-test={`${gridType}-container`}
      {...(isVertical ? { stack: 'xs' } : { row: 'xs' })}
      gutterSize={theme.channel === 'sainsburys' ? theme.size.spacing.xl : theme.size.spacing.lg}
      isVertical={isVertical}
      consumingGridType={consumingGridType}
      hasScroll={hasScroll}
      numberOfComponents={numberOfComponents}
    >
      {content}
    </Container>
  )

  if (hasScroll && consumingGridType !== 'ug004') {
    return (
      <SliderWrapper
        data-test={`${gridType}-slider-wrapper`}
        negative='xs'
        gutterSize='0'
        consumingGridType={consumingGridType}
      >
        {gridContainer}
      </SliderWrapper>
    )
  }

  return gridContainer
}

UGLayout.propTypes = {
  categoryId: PropTypes.string,
  consumingGridType: PropTypes.string.isRequired,
  included: PropTypes.array,
  imageStyle: PropTypes.string,
  onLinkClick: PropTypes.func,
  mobileLayout: PropTypes.oneOf(['slider', 'stack']),
  layout: PropTypes.oneOf(['slider', 'stack']),
  flipOrientation: PropTypes.bool,
  isVertical: PropTypes.bool,
  childrenArray: PropTypes.node
}

UGLayout.defaultProps = {
  categoryId: '',
  onLinkClick: null,
  mobileLayout: 'stack',
  layout: 'stack',
  flipOrientation: false,
  isVertical: false,
  imageStyle: ''
}

export default withTheme(UGLayout)
